import React, { useState } from 'react';
import { useDashboardContext } from '@pages/DashboardLayout';
import customFetch from '@utils/customFetch';
import { toast } from 'react-toastify';
import PrimaryButton from '@components/PrimaryButton';
import SecondaryButton from '@components/SecondaryButton';

const WalletDownload = () => {
  const { user } = useDashboardContext();
  const [loadingApple, setLoadingApple] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);

  const handleAppleWalletDownload = async () => {
    try {
      setLoadingApple(true);
      const response = await customFetch.get(
        `/wallet/apple-passbook/${user._id}`,
        {
          responseType: 'blob',
        }
      );

      // Create blob from response
      const blob = new Blob([response.data], {
        type: 'application/vnd.apple.pkpass',
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contact_${user.lastName}.pkpass`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Apple Wallet Pass wurde heruntergeladen');
    } catch (error) {
      console.error('Error downloading Apple Wallet pass:', error);
      toast.error('Fehler beim Herunterladen des Apple Wallet Pass');
    } finally {
      setLoadingApple(false);
    }
  };

  const handleGoogleWalletDownload = async () => {
    try {
      setLoadingGoogle(true);
      const response = await customFetch.get(
        `/wallet/google-wallet/${user._id}`
      );

      if (response.data.success && response.data.saveUrl) {
        // Open Google Wallet add page in new tab
        window.open(response.data.saveUrl, '_blank');
        toast.success('Google Wallet wird geöffnet');
      } else {
        toast.error('Fehler beim Generieren des Google Wallet Pass');
      }
    } catch (error) {
      console.error('Error generating Google Wallet pass:', error);
      toast.error('Fehler beim Generieren des Google Wallet Pass');
    } finally {
      setLoadingGoogle(false);
    }
  };

  return (
    <div className="bg-white px-6 py-8 sm:py-10 lg:px-8">
      <div className="mx-auto max-w-2xl">
        <h3 className="text-lg font-semibold leading-7 text-gray-900 mb-6">
          Wallet Download
        </h3>
        <p className="text-sm text-gray-600 mb-8">
          Füge dein sVisit-Profil zu deinem Apple Wallet oder Google Wallet
          hinzu.
        </p>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {/* Apple Wallet */}
          <div className="rounded-lg border border-gray-300 p-6 hover:border-svisit-primary-400 transition-colors">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4">
                <svg
                  className="h-16 w-16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
              </div>
              <h4 className="text-base font-semibold text-gray-900 mb-2">
                Apple Wallet
              </h4>
              <p className="text-sm text-gray-500 mb-4">
                Für iPhone und Apple Watch
              </p>
              <button
                onClick={handleAppleWalletDownload}
                disabled={loadingApple}
                className="inline-flex w-full justify-center rounded-md bg-svisit-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 disabled:opacity-50 disabled:cursor-not-allowed hover:cursor-pointer"
              >
                {loadingApple ? 'Wird generiert...' : 'Zu Apple Wallet'}
              </button>
            </div>
          </div>

          {/* Google Wallet */}
          <div className="rounded-lg border border-gray-300 p-6 hover:border-svisit-primary-400 transition-colors">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4">
                <svg
                  className="h-16 w-16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M21.5 7.5h-3V6c0-.825-.675-1.5-1.5-1.5H7c-.825 0-1.5.675-1.5 1.5v1.5h-3C1.675 7.5 1 8.175 1 9v9c0 .825.675 1.5 1.5 1.5h19c.825 0 1.5-.675 1.5-1.5V9c0-.825-.675-1.5-1.5-1.5zM7 6h10v1.5H7V6zM2.5 9h19v9h-19V9z" />
                  <path d="M11.5 13.5h1v3h-1v-3z" />
                </svg>
              </div>
              <h4 className="text-base font-semibold text-gray-900 mb-2">
                Google Wallet
              </h4>
              <p className="text-sm text-gray-500 mb-4">Für Android-Geräte</p>
              <button
                onClick={handleGoogleWalletDownload}
                disabled={loadingGoogle}
                className="inline-flex w-full justify-center rounded-md bg-svisit-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 disabled:opacity-50 disabled:cursor-not-allowed hover:cursor-pointer"
              >
                {loadingGoogle ? 'Wird generiert...' : 'Zu Google Wallet'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletDownload;
